'use client'

import { convertSurveyQuestions } from '@/components/scenario/utils/scenarioToPlayerConfig'
import { PlaybackSnapshot, useRestorePlaybackOnReload } from '@/hooks/useRestorePlaybackOnReload'
import { trackExternalLink, trackModalClose, trackPageLeave, trackVideoEnd, trackVideoStart } from '@/lib/analytics'
import { usePageHide, usePlayedSecondsCounter } from '@/lib/playedSeconds'
import { resolveEffectiveCta, type CtaFields } from '@/lib/resolveCta'
import { hasSkippedVideoFeedback, hasVotedVideo, markSkippedVideoFeedback } from '@/lib/videoFeedback'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useShadowHostPortal } from '../shadow-host-portal/ShadowHostPortalContext'
import { useFrameFullscreenExit } from '../shadow-host-portal/useFrameFullscreenExit'
import FloatingPiP from './floating-pip'
import NextVideoPreloader from './NextVideoPreloader'
import PCVideoModal from './PCVideoModal'
import SPVideoModal from './sp-video-modal'
import { useModalSurvey } from './useModalSurvey'
import { DEFAULT_EMBED_FONT, ensureEmbedFontLoaded, type EmbedFont } from '@/components/scenario/embedFont'

// PCモーダルはプレイリスト・動画・詳細の3列構成のため、タブレット縦幅では
// 動画領域が狭くなる。1024px未満はSP版の全画面動画＋下部CTAレイアウトを使う。
const SP_BREAKPOINT = 1024

export interface VideoModalProps {
  nodes: ScenarioNodeWithVideoUrl[]
  videoOrder: string[]
  initialVideoIndex: number
  isOpen: boolean
  onClose: () => void
  onVideoChange?: (nodeId: string, index: number) => void
  onRequestOpen?: (videoIndex: number) => void
  onPiPActiveChange?: (active: boolean) => void
  scenarioId?: string
  /** プレイリスト共通CTA（SLIDER/TILE限定・動画CTA未設定時の既定値・ADR-0019） */
  ctaUrl?: string | null
  ctaLabel?: string | null
  /** 管理画面プレビュー。分析・回答データを送信しない。 */
  isPreview?: boolean
  /** 視聴モーダルのフォント（プレイリスト単位の設定）。既定はゴシック。 */
  embedFont?: EmbedFont
}

const VideoModal: FC<VideoModalProps> = ({
  nodes,
  videoOrder,
  initialVideoIndex,
  isOpen,
  onClose,
  onVideoChange,
  onRequestOpen,
  onPiPActiveChange,
  scenarioId,
  ctaUrl,
  ctaLabel,
  isPreview = false,
  embedFont = DEFAULT_EMBED_FONT,
}) => {
  const [isSP, setIsSP] = useState(false)
  const [currentVideoIndex, setCurrentVideoIndex] = useState(initialVideoIndex)
  const [showEndScreen, setShowEndScreen] = useState(false)
  const [showPiP, setShowPiP] = useState(false)
  const [pipVideoUrl, setPipVideoUrl] = useState<string | null>(null)
  const [pipCurrentTime, setPipCurrentTime] = useState(0)
  const [pipVideoIndex, setPipVideoIndex] = useState(0)
  const [pipIsMuted, setPipIsMuted] = useState(false)
  const [reopenTime, setReopenTime] = useState<number | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  /**
   * マウント起点の自動再生を済ませた動画（nodeKey）。
   *
   * PC/SP は別コンポーネントなので、`isSP` が切り替わると <video> が作り直され、
   * 各モーダルの「マウントしたら再生する」処理が視聴者の一時停止を無視して走る。
   * 作り直されるのは子なので、フラグは切替をまたいで残る親が持つ。
   */
  const autoPlayedNodeKeyRef = useRef<string | null>(null)
  /** 作り直しをまたいで再生位置と再生/停止を引き継ぐための控え（useRestorePlaybackOnReload に渡す） */
  const playbackStateRef = useRef<PlaybackSnapshot | null>(null)
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const videoStartTimeRef = useRef<number>(Date.now())
  const prevIsOpenRef = useRef(false)
  // 実際に再生した秒数を数える（一時停止・放置・早送りは数えない）
  const playedCounter = usePlayedSecondsCounter(() => videoRef.current, isOpen)

  useEffect(() => {
    onPiPActiveChange?.(showPiP)
  }, [showPiP, onPiPActiveChange])

  // 選択されたフォントの Web フォントを読み込む。既定（ゴシック）と OS標準は追加読み込み不要なので
  // ここでは何も起きない。埋め込み先サイトへのリクエストを増やさないため、isOpen を待たず
  // mount 時に投げてモーダルを開いた瞬間の差し替えチラつきを避ける。
  useEffect(() => {
    ensureEmbedFontLoaded(embedFont)
  }, [embedFont])

  // Order nodes by videoOrder
  const orderedNodes = useMemo(
    () =>
      videoOrder && videoOrder.length > 0
        ? videoOrder
            .map((videoId) => nodes.find((node) => node.videoId === videoId))
            .filter((node): node is ScenarioNodeWithVideoUrl => node !== undefined)
        : nodes,
    [videoOrder, nodes],
  )

  const currentNode = orderedNodes[currentVideoIndex]

  /**
   * 止めたところからそのまま戻れるようにする。次の2つをまとめて扱う。
   * 1. 別タブから戻ってメディアが読み込み直された（同じ <video> のまま）
   * 2. PC/SP の出し分けが切り替わり <video> が別要素に差し替わった
   * 2 を跨ぐため、控えは親であるここに置く。
   */
  useRestorePlaybackOnReload(videoRef, {
    // hls.js 経由だと currentSrc が blob: で毎回変わるため、判定キーはノードキーを使う
    mediaKey: currentNode?.nodeKey,
    store: playbackStateRef,
    resetToken: isSP,
  })
  // 次動画の先読み用URL（最後の動画なら null）。出だしを低優先で先読みして切替を高速化する。
  const nextVideoUrl =
    currentVideoIndex < orderedNodes.length - 1 ? orderedNodes[currentVideoIndex + 1].videoUrl : null
  // 動画CTA が設定されていればそれを使い、無ければプレイリスト共通CTA を既定値にする（ADR-0019）。
  // 優先順位とラベルのペア揃えは resolveEffectiveCta に一元化（動画詳細ページと共通）。
  const { ctaUrl: effectiveCtaUrl, ctaLabel: effectiveCtaLabel } = resolveEffectiveCta(
    { ctaUrl, ctaLabel },
    currentNode?.video as CtaFields | undefined,
  )

  // アンケート（#244）: cue 同期・回答保存は #238 のロジックを再利用。
  // 描画は PC=右パネル / SP=下シート（commonProps 経由で各モーダルへ渡す）。
  const surveyQuestions = useMemo(
    () => convertSurveyQuestions(currentNode?.surveyQuestions),
    [currentNode?.surveyQuestions],
  )
  const { survey, pendingEndCount } = useModalSurvey({
    questions: surveyQuestions,
    nodeKey: currentNode?.nodeKey ?? '',
    videoRef,
    target: scenarioId ? { kind: 'embed', scenarioId } : undefined,
    active: isOpen && !showEndScreen,
  })

  // Check viewport size
  useEffect(() => {
    const handleResize = () => {
      clearTimeout(resizeTimerRef.current)
      resizeTimerRef.current = setTimeout(() => {
        setIsSP(window.innerWidth < SP_BREAKPOINT)
      }, 150)
    }
    setIsSP(window.innerWidth < SP_BREAKPOINT)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimerRef.current)
    }
  }, [])

  // Reset state when modal opens; close PiP immediately if active
  useEffect(() => {
    if (isOpen) {
      setCurrentVideoIndex(initialVideoIndex)
      setShowEndScreen(false)
      // Close PiP immediately when a new video is selected
      if (showPiP) {
        setShowPiP(false)
        setPipVideoUrl(null)
        setPipCurrentTime(0)
      }
      // 開き直しは最初から見せる。前回の視聴状態は引き継がない
      autoPlayedNodeKeyRef.current = null
      playbackStateRef.current = null
      // 前回 close 時に opacity=0 にしていたら戻す
      if (videoRef.current) {
        videoRef.current.style.opacity = ''
      }
    }
  }, [isOpen, initialVideoIndex])

  // Track VIDEO_START when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && scenarioId) {
      const node = orderedNodes[initialVideoIndex]
      if (node) {
        videoStartTimeRef.current = Date.now()
        playedCounter.reset()
        trackVideoStart(scenarioId, node.nodeKey, node.video?.duration ?? undefined)
      }
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, initialVideoIndex, scenarioId, orderedNodes])

  // Full close: stop video and hide modal completely (no PiP)
  const handleFullClose = useCallback(() => {
    if (scenarioId && currentNode) {
      const viewTime = (Date.now() - videoStartTimeRef.current) / 1000
      trackModalClose(scenarioId, currentNode.nodeKey, viewTime, playedCounter.take())
    }
    if (videoRef.current) {
      // ネイティブコントロールの中央 ▶︎ オーバーレイ（一時停止時に表示される）が一瞬出るのを防ぐため
      // pause より先に video 要素を不可視にする
      videoRef.current.style.opacity = '0'
      videoRef.current.pause()
    }
    // Ensure PiP is not shown
    setShowPiP(false)
    setPipVideoUrl(null)
    setPipCurrentTime(0)
    setPipIsMuted(false)
    onPiPActiveChange?.(false)
    onClose()
  }, [onClose, onPiPActiveChange, scenarioId, currentNode])

  // モーダルを開いたままブラウザのタブごと閉じられたときも、見た秒数を送る
  usePageHide(() => {
    if (!scenarioId || !currentNode) return
    const viewTime = (Date.now() - videoStartTimeRef.current) / 1000
    trackPageLeave(scenarioId, currentNode.nodeKey, viewTime, playedCounter.take())
  }, isOpen)

  // PiP transition: save state and transition to PiP (used by backdrop tap on SP, and as default close on PC)
  const handleClose = useCallback(() => {
    if (scenarioId && currentNode) {
      const viewTime = (Date.now() - videoStartTimeRef.current) / 1000
      trackModalClose(scenarioId, currentNode.nodeKey, viewTime, playedCounter.take())
    }
    if (videoRef.current && !videoRef.current.paused) {
      // Save current video state for PiP
      setPipVideoUrl(currentNode?.videoUrl || null)
      setPipCurrentTime(videoRef.current.currentTime)
      setPipIsMuted(videoRef.current.muted)
      setPipVideoIndex(currentVideoIndex)
      setShowPiP(true)
      onPiPActiveChange?.(true)
    } else {
      onPiPActiveChange?.(false)
    }
    if (videoRef.current) {
      // ネイティブコントロールの中央 ▶︎ オーバーレイが一瞬出るのを防ぐ
      videoRef.current.style.opacity = '0'
    }
    onClose()
  }, [currentNode?.videoUrl, currentVideoIndex, onClose, onPiPActiveChange, scenarioId, currentNode])

  // iframe 埋め込みページ（/embed）では「閉じる」を縮小再生（PiP）へ逃がさず完全に閉じる。
  // モーダルの間だけ iframe を全画面化しているので、PiP に移ると黒い全画面の隅に
  // 小窓だけが残る（親ページに戻れない）ため。script 埋め込みでは従来どおり PiP へ移る。
  const isFrameEmbed = useShadowHostPortal()?.isFrameEmbed ?? false
  const handleRequestClose = isFrameEmbed ? handleFullClose : handleClose

  // iframe 埋め込みページで、ブラウザ側の操作（Esc / 全画面を終了）で全画面が解除されたら閉じる
  useFrameFullscreenExit(isOpen, handleFullClose)

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleRequestClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleRequestClose])

  // Handle PiP close
  const handlePiPClose = useCallback(() => {
    setShowPiP(false)
    setPipVideoUrl(null)
    setPipCurrentTime(0)
    setPipIsMuted(false)
    onPiPActiveChange?.(false)
  }, [onPiPActiveChange])

  // Handle PiP reopen: click PiP to reopen modal and resume from current time
  const handlePiPReopen = useCallback(
    (time: number) => {
      setReopenTime(time)
      setShowPiP(false)
      setPipVideoUrl(null)
      setPipCurrentTime(0)
      onPiPActiveChange?.(false)
      setShowEndScreen(false)
      // Tell parent to open modal at the PiP video index
      onRequestOpen?.(pipVideoIndex)
    },
    [onPiPActiveChange, pipVideoIndex, onRequestOpen],
  )

  // Handle video navigation
  const handlePreviousVideo = useCallback(() => {
    if (currentVideoIndex > 0) {
      // 途中で別の動画へ移るときは「最後まで再生」を送らず、見た秒数を次の動画の開始に添える
      const previous = currentNode
        ? { nodeKey: currentNode.nodeKey, played: playedCounter.take() }
        : undefined
      const newIndex = currentVideoIndex - 1
      const newNode = orderedNodes[newIndex]
      if (scenarioId && newNode) {
        videoStartTimeRef.current = Date.now()
        playedCounter.reset()
        trackVideoStart(scenarioId, newNode.nodeKey, newNode.video?.duration ?? undefined, previous)
      }
      setCurrentVideoIndex(newIndex)
      setShowEndScreen(false)
      onVideoChange?.(orderedNodes[newIndex].id, newIndex)
    }
  }, [currentVideoIndex, orderedNodes, onVideoChange, scenarioId, currentNode])

  const handleNextVideo = useCallback(() => {
    if (currentVideoIndex < orderedNodes.length - 1) {
      // 途中で別の動画へ移るときは「最後まで再生」を送らず、見た秒数を次の動画の開始に添える
      const previous = currentNode
        ? { nodeKey: currentNode.nodeKey, played: playedCounter.take() }
        : undefined
      const newIndex = currentVideoIndex + 1
      const newNode = orderedNodes[newIndex]
      if (scenarioId && newNode) {
        videoStartTimeRef.current = Date.now()
        playedCounter.reset()
        trackVideoStart(scenarioId, newNode.nodeKey, newNode.video?.duration ?? undefined, previous)
      }
      setCurrentVideoIndex(newIndex)
      setShowEndScreen(false)
      onVideoChange?.(orderedNodes[newIndex].id, newIndex)
    }
  }, [currentVideoIndex, orderedNodes, onVideoChange, scenarioId, currentNode])

  // Handle video selection from playlist
  const handleVideoSelect = useCallback(
    (index: number) => {
      // 途中で別の動画へ移るときは「最後まで再生」を送らず、見た秒数を次の動画の開始に添える
      const previous = currentNode
        ? { nodeKey: currentNode.nodeKey, played: playedCounter.take() }
        : undefined
      const newNode = orderedNodes[index]
      if (scenarioId && newNode) {
        videoStartTimeRef.current = Date.now()
        playedCounter.reset()
        trackVideoStart(scenarioId, newNode.nodeKey, newNode.video?.duration ?? undefined, previous)
      }
      setCurrentVideoIndex(index)
      setShowEndScreen(false)
      onVideoChange?.(orderedNodes[index].id, index)
    },
    [orderedNodes, onVideoChange, scenarioId, currentNode],
  )

  // Handle "another video" button (go to next video; loops to first when at the end,
  // which also covers the single-video case by restarting the same video)
  const handleAnotherVideo = useCallback(() => {
    if (currentVideoIndex < orderedNodes.length - 1) {
      handleNextVideo()
    } else {
      // Loop back to first video
      const newNode = orderedNodes[0]
      if (scenarioId && newNode) {
        videoStartTimeRef.current = Date.now()
        playedCounter.reset()
        trackVideoStart(scenarioId, newNode.nodeKey, newNode.video?.duration ?? undefined)
      }
      setCurrentVideoIndex(0)
      setShowEndScreen(false)
      onVideoChange?.(orderedNodes[0].id, 0)
    }
  }, [currentVideoIndex, orderedNodes, handleNextVideo, onVideoChange, scenarioId])

  // 動画評価を閉じたあと、同じ動画を頭から再生し直すかどうか。
  // 回答後（サンクス表示のあと）は再生に戻すが、スキップは「答えたくない」の合図なので
  // 勝手に再生し直さない（SPでは音が鳴り直すうえ、終了→評価→スキップ→終了…と巡回する）。
  const [replayAfterEndScreen, setReplayAfterEndScreen] = useState(true)

  // 動画評価を回答後に閉じる。次の動画へ飛ばさず、同じ動画をもう一度再生する。
  // 「別の動画へ」「CTA」は自分で選べるようにしておく（SP埋め込みの導線を塞がないため）。
  const handleDismissEndScreen = useCallback(() => {
    setReplayAfterEndScreen(true)
    setShowEndScreen(false)
  }, [])

  // 動画評価をスキップして閉じる。動画は終了状態のまま止め、次に何をするかは視聴者に委ねる
  // （中央の再生ボタンでもう一度見る / 別の動画へ / CTA）。
  const handleSkipEndScreen = useCallback(() => {
    if (!isPreview && currentNode?.videoId) {
      markSkippedVideoFeedback(currentNode.videoId)
    }
    setReplayAfterEndScreen(false)
    setShowEndScreen(false)
  }, [currentNode, isPreview])

  // この動画の評価を回答済み・スキップ済みか（評価画面をもう出さない）
  const isFeedbackDone = useCallback(() => {
    const videoId = currentNode?.videoId
    if (isPreview || !videoId) return false
    return hasVotedVideo(videoId) || hasSkippedVideoFeedback(videoId)
  }, [currentNode, isPreview])

  // 動画末尾の終了処理: 動画評価が未回答ならフィードバック画面、回答済み・スキップ済みなら次動画へ（連続再生）。
  const proceedAfterVideoEnd = useCallback(() => {
    if (isFeedbackDone()) {
      handleAnotherVideo()
      return
    }
    setShowEndScreen(true)
  }, [handleAnotherVideo, isFeedbackDone])

  // 末尾(null-cue)の未回答アンケートを終了処理の前に見せるための待機フラグ
  const awaitingEndSurveyRef = useRef(false)

  // Handle video end
  const handleVideoEnd = useCallback(() => {
    if (scenarioId && currentNode) {
      const viewTime = (Date.now() - videoStartTimeRef.current) / 1000
      trackVideoEnd(scenarioId, currentNode.nodeKey, viewTime, playedCounter.take())
    }
    // 末尾でまとめて提示する未回答アンケートがあれば、動画評価より先にアンケートを出す。
    // 回答が済んだら下の effect が終了処理を続行する。
    if (pendingEndCount > 0) {
      awaitingEndSurveyRef.current = true
      return
    }
    proceedAfterVideoEnd()
  }, [scenarioId, currentNode, pendingEndCount, proceedAfterVideoEnd])

  // 末尾アンケートの回答が済んだら、保留していた終了処理へ進む。
  // ただしアンケートの送信で別の動画へは進ませない（動画が終わったあとの画面の操作で別の動画へ進む形は
  // 作らないため・ADR-0023 A-2）。評価が未回答なら評価画面、済んでいれば動画は終了状態のまま止め、
  // もう一度見る／別の動画へ／CTA は視聴者に委ねる。
  useEffect(() => {
    if (awaitingEndSurveyRef.current && pendingEndCount === 0) {
      awaitingEndSurveyRef.current = false
      if (isFeedbackDone()) return
      setShowEndScreen(true)
    }
  }, [pendingEndCount, isFeedbackDone])

  // Apply resume time when modal opens from PiP reopen
  useEffect(() => {
    if (isOpen && reopenTime !== null && videoRef.current) {
      const video = videoRef.current
      const applyResumeTime = () => {
        video.currentTime = reopenTime
        video.play().catch(console.error)
        setReopenTime(null)
      }
      // Wait for video to be ready
      if (video.readyState >= 1) {
        applyResumeTime()
      } else {
        video.addEventListener('loadedmetadata', applyResumeTime, { once: true })
        return () => video.removeEventListener('loadedmetadata', applyResumeTime)
      }
    }
  }, [isOpen, reopenTime])

  // CTA click tracking handler
  const handleTrackCtaClick = useCallback(
    (nodeKey: string, url: string, label?: string) => {
      /* v8 ignore next 3 */
      if (scenarioId) {
        trackExternalLink(scenarioId, nodeKey, url, label, playedCounter.take())
      }
    },
    [scenarioId],
  )

  const commonProps = useMemo(
    () => ({
      nodes: orderedNodes,
      currentNode,
      currentVideoIndex,
      videoRef,
      autoPlayedNodeKeyRef,
      showEndScreen,
      onClose: handleRequestClose,
      onFullClose: handleFullClose,
      onPreviousVideo: handlePreviousVideo,
      onNextVideo: handleNextVideo,
      onVideoSelect: handleVideoSelect,
      onVideoEnd: handleVideoEnd,
      onDismissEndScreen: handleDismissEndScreen,
      onSkipEndScreen: handleSkipEndScreen,
      replayAfterEndScreen,
      ctaUrl: effectiveCtaUrl,
      ctaLabel: effectiveCtaLabel,
      onTrackCtaClick: scenarioId ? handleTrackCtaClick : undefined,
      scenarioId,
      isPreview,
      survey,
      embedFont,
    }),
    [
      orderedNodes,
      currentNode,
      currentVideoIndex,
      videoRef,
      showEndScreen,
      handleRequestClose,
      handleFullClose,
      handlePreviousVideo,
      handleNextVideo,
      handleVideoSelect,
      handleVideoEnd,
      handleDismissEndScreen,
      handleSkipEndScreen,
      replayAfterEndScreen,
      effectiveCtaUrl,
      effectiveCtaLabel,
      scenarioId,
      isPreview,
      handleTrackCtaClick,
      survey,
      embedFont,
    ],
  )

  return (
    <>
      {/* Custom Floating PiP */}
      <FloatingPiP
        videoUrl={pipVideoUrl}
        currentTime={pipCurrentTime}
        isMuted={pipIsMuted}
        isOpen={showPiP}
        onClose={handlePiPClose}
        onReopen={handlePiPReopen}
      />

      {/* Modal */}
      {isOpen && currentNode && (
        <>
          {isSP ? <SPVideoModal {...commonProps} /> : <PCVideoModal {...commonProps} />}
          {/* 次動画の出だしを低優先で先読み（切替の高速化） */}
          <NextVideoPreloader videoUrl={nextVideoUrl} />
        </>
      )}
    </>
  )
}

export default VideoModal
